/**
 * @module rybbit-events
 * @description Fires Rybbit ecommerce analytics events for the Stardust theme.
 *
 * Events tracked:
 * - view_item: on product pages from [data-rybbit-product]
 * - add_to_cart: on confirmed cart:updated after cart:add-start
 * - begin_checkout: on [data-checkout-submit] click using cached cart state
 *
 * No-ops when window.rybbit is unavailable.
 *
 * @listens cart:add-start
 * @listens cart:updated
 * @listens click [data-checkout-submit]
 */

import { CART_EVENTS } from '@stardust/cart';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** @param {string} name @param {Object} data */
function track(name, data) {
  if (window.rybbit) window.rybbit.event(name, data);
}

/** @param {Object} item — Shopify cart line item @returns {Object} */
function mapCartItem(item) {
  return {
    item_id: item.sku || String(item.variant_id),
    item_name: item.product_title,
    item_variant: item.variant_title !== 'Default Title' ? item.variant_title : null,
    price: item.final_price / 100,
    quantity: item.quantity,
  };
}

// ── State ─────────────────────────────────────────────────────────────────────

/** @type {Object|null} */
let cachedCart = null;

/** @type {number|null} */
let pendingAddVariantId = null;

/** Guard: fire begin_checkout at most once per page load. */
let checkoutTracked = false;

// Seed the cache on the cart page (/cart), where [data-checkout-submit] is
// present in the initial DOM and no cart:updated will fire before checkout.
// On all other pages the cart drawer button is injected lazily, so the
// cart:updated listener below keeps the cache current instead.
if (document.querySelector('[data-checkout-submit]')) {
  fetch('/cart.js', { credentials: 'same-origin' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => { if (data) cachedCart = data; })
    .catch(() => {});
}

// ── view_item ─────────────────────────────────────────────────────────────────
// Both this module (type="module") and Rybbit's script (defer) execute in
// source order after parsing. A minimal defer ensures Rybbit has initialised.

requestAnimationFrame(() => {
  const el = document.querySelector('[data-rybbit-product]');
  if (!el) return;
  try {
    const data = JSON.parse(el.textContent);
    data.item_id = String(data.item_id);
    track('view_item', data);
  } catch { /* malformed JSON — silent */ }
});

// ── Cart cache + add_to_cart ──────────────────────────────────────────────────

document.addEventListener(CART_EVENTS.addStart, (e) => {
  const { variantId } = e.detail || {};
  if (variantId) pendingAddVariantId = variantId;
});

document.addEventListener(CART_EVENTS.addFailed, () => {
  pendingAddVariantId = null;
});

document.addEventListener(CART_EVENTS.updated, (e) => {
  const cart = e.detail;
  if (!cart) return;

  cachedCart = cart;

  if (!pendingAddVariantId) return;
  const variantId = pendingAddVariantId;
  pendingAddVariantId = null;

  const cartItem = cart.items.find((i) => i.variant_id === variantId);
  if (!cartItem) return;

  const mapped = mapCartItem(cartItem);
  mapped.currency = cart.currency;
  track('add_to_cart', mapped);
});

// ── begin_checkout ────────────────────────────────────────────────────────────

document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-checkout-submit]')) return;
  if (!cachedCart || !cachedCart.items.length) return;
  if (checkoutTracked) return;
  checkoutTracked = true;

  track('begin_checkout', {
    currency: cachedCart.currency,
    value: cachedCart.total_price / 100,
    items: cachedCart.items.map(mapCartItem),
  });
});
